/**
 * ==== STANDALONE:FARMHAND_LITE:START ====
 * Feature: Weather forecast widget for farm planning
 * Tier: Lite ($3/mo)
 * Dependencies: Weather API service
 * Generalization:
 *   - Replace bridgeClubAPI with standalone weather service
 *   - Remove RB store links
 *   - Keep: forecast display, hourly view, conditions, wind
 * ============================================
 */
import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { bridgeClubAPI } from '../../services/bridgeClubAPI';
import './WeatherWidget.css';

interface Forecast {
  date: string;
  name: string;
  short_name?: string;
  high: number;
  low: number;
  wind_speed: number;
  wind_direction: string;
  precipitation_chance: number;
  conditions: string;
  detailed: string;
  humidity?: number;
}

interface HourlyForecast {
  time: string;
  hour: number;
  temp: number;
  conditions: string;
  wind_speed: number;
  wind_direction: string;
  precipitation_chance: number;
  is_daytime: boolean;
}

interface CurrentConditions {
  temp: number;
  feels_like: number;
  conditions: string;
  wind_speed: number;
  wind_direction: string;
  humidity: number | null;
  precipitation_chance: number;
}

interface ChecklistItem {
  task: string;
  priority: 'high' | 'medium' | 'low';
}

interface AnimalAlert {
  type: string;
  animal: {
    id: number;
    category: string;
    animal_type: string;
    name: string;
    count: number;
  };
  icon: string;
  title: string;
  subtitle: string;
  checklist: ChecklistItem[];
  feed_tip: string | null;
}

interface AnimalAlertSimple {
  icon: string;
  severity: 'info' | 'warning';
  message: string;
}

interface WeatherData {
  location: {
    city: string;
    state: string;
  };
  forecasts: Forecast[];
  animal_alerts: AnimalAlertSimple[];
  error?: string;
  message?: string;
}

interface DetailedWeatherData {
  location: {
    city: string;
    state: string;
  };
  current: CurrentConditions;
  hourly: HourlyForecast[];
  daily: Forecast[];
  sun: {
    sunrise: string;
    sunset: string;
  };
  alerts: AnimalAlert[];
  general_tips: string[];
  animals: Array<{
    id: number;
    category: string;
    animal_type: string;
    name: string;
    count: number;
  }>;
}

const getWeatherIcon = (conditions: string, isDay: boolean = true): string => {
  const lower = conditions.toLowerCase();
  if (lower.includes('sun') || lower.includes('clear')) return isDay ? '☀️' : '🌙';
  if (lower.includes('cloud') && lower.includes('part')) return isDay ? '⛅' : '☁️';
  if (lower.includes('cloud')) return '☁️';
  if (lower.includes('rain') && lower.includes('thunder')) return '⛈️';
  if (lower.includes('rain') || lower.includes('shower')) return '🌧️';
  if (lower.includes('snow')) return '❄️';
  if (lower.includes('fog') || lower.includes('mist')) return '🌫️';
  if (lower.includes('wind')) return '💨';
  return isDay ? '🌤️' : '🌙';
};

const WeatherWidget: React.FC = () => {
  const [weather, setWeather] = useState<WeatherData | null>(null);
  const [detailedWeather, setDetailedWeather] = useState<DetailedWeatherData | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadingDetailed, setLoadingDetailed] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const [selectedAlert, setSelectedAlert] = useState<string | null>(null);
  const [checkedItems, setCheckedItems] = useState<Set<string>>(new Set());

  useEffect(() => {
    loadWeather();
  }, []);

  const loadWeather = async () => {
    try {
      setLoading(true);
      const data = await bridgeClubAPI.getWeatherForecast();
      setWeather(data);
    } catch (err) {
      console.error('Failed to load weather:', err);
      setWeather(null);
    } finally {
      setLoading(false);
    }
  };

  const loadDetailedWeather = async () => {
    if (detailedWeather) return; // Already loaded
    try {
      setLoadingDetailed(true);
      const data = await bridgeClubAPI.getWeatherDetailed();
      setDetailedWeather(data);
    } catch (err) {
      console.error('Failed to load detailed weather:', err);
    } finally {
      setLoadingDetailed(false);
    }
  };

  const handleExpand = () => {
    if (!expanded) {
      loadDetailedWeather();
    }
    setExpanded(!expanded);
  };

  const toggleCheckItem = (alertId: string, taskIndex: number) => {
    const key = `${alertId}-${taskIndex}`;
    setCheckedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(key)) {
        newSet.delete(key);
      } else {
        newSet.add(key);
      }
      return newSet;
    });
  };

  if (loading) {
    return (
      <div className="weather-widget weather-loading">
        <div className="weather-header">
          <span className="weather-icon-header">🌤️</span>
          <span className="weather-title">Weather</span>
        </div>
        <div className="weather-loading-text">Loading...</div>
      </div>
    );
  }

  if (!weather || weather.error || !weather.forecasts?.length) {
    return (
      <div className="weather-widget weather-no-data">
        <div className="weather-header">
          <span className="weather-icon-header">🌤️</span>
          <span className="weather-title">Weather</span>
        </div>
        <div className="weather-no-data-content">
          <p>{weather?.message || 'Weather data unavailable'}</p>
          {weather?.error === 'No location set' && (
            <Link to="/bridge-club/profile" className="weather-setup-link">
              Set up location →
            </Link>
          )}
        </div>
      </div>
    );
  }

  const today = weather.forecasts[0];
  const upcoming = weather.forecasts.slice(1);

  return (
    <div className={`weather-widget ${expanded ? 'weather-expanded' : ''}`}>
      {/* Header */}
      <div className="weather-header" onClick={handleExpand} style={{ cursor: 'pointer' }}>
        <div className="weather-header-left">
          <span className="weather-icon-header">🌤️</span>
          <span className="weather-title">Weather</span>
          {weather.location.city && (
            <span className="weather-location">
              {weather.location.city}, {weather.location.state}
            </span>
          )}
        </div>
        <button
          className="weather-expand-btn"
          onClick={(e) => { e.stopPropagation(); handleExpand(); }}
        >
          {expanded ? '−' : '+'}
        </button>
      </div>

      {/* Today's Weather Summary */}
      <div className="weather-today" onClick={handleExpand} style={{ cursor: 'pointer' }}>
        <div className="weather-today-icon">
          {getWeatherIcon(today.conditions)}
        </div>
        <div className="weather-today-temps">
          <span className="weather-high">{today.high}°</span>
          <span className="weather-low">{today.low}°</span>
        </div>
        <div className="weather-today-conditions">
          <span className="weather-conditions-text">{today.conditions}</span>
          {today.precipitation_chance > 20 && (
            <span className="weather-precip">
              💧 {today.precipitation_chance}%
            </span>
          )}
        </div>
        <span className="weather-expand-hint">{expanded ? '▼' : '▶'}</span>
      </div>

      {/* Expanded View */}
      {expanded && (
        <div className="weather-expanded-content">
          {loadingDetailed ? (
            <div className="weather-loading-detailed">Loading detailed forecast...</div>
          ) : detailedWeather ? (
            <>
              {/* Current Conditions */}
              {detailedWeather.current && (
                <div className="weather-current-details">
                  <h4>Current Conditions</h4>
                  <div className="weather-details-grid">
                    <div className="weather-detail-item">
                      <span className="detail-label">Feels Like</span>
                      <span className="detail-value">{detailedWeather.current.feels_like}°</span>
                    </div>
                    <div className="weather-detail-item">
                      <span className="detail-label">Wind</span>
                      <span className="detail-value">
                        {detailedWeather.current.wind_speed} mph {detailedWeather.current.wind_direction}
                      </span>
                    </div>
                    {detailedWeather.current.humidity !== null && (
                      <div className="weather-detail-item">
                        <span className="detail-label">Humidity</span>
                        <span className="detail-value">{detailedWeather.current.humidity}%</span>
                      </div>
                    )}
                    <div className="weather-detail-item">
                      <span className="detail-label">Precip</span>
                      <span className="detail-value">{detailedWeather.current.precipitation_chance}%</span>
                    </div>
                  </div>
                  {detailedWeather.sun && (
                    <div className="weather-sun-times">
                      <span>🌅 {detailedWeather.sun.sunrise}</span>
                      <span>🌇 {detailedWeather.sun.sunset}</span>
                    </div>
                  )}
                </div>
              )}

              {/* Hourly Forecast */}
              {detailedWeather.hourly && detailedWeather.hourly.length > 0 && (
                <div className="weather-hourly">
                  <h4>Next 12 Hours</h4>
                  <div className="hourly-scroll">
                    {detailedWeather.hourly.map((hour, idx) => (
                      <div key={idx} className="hourly-item">
                        <span className="hourly-time">{hour.time}</span>
                        <span className="hourly-icon">
                          {getWeatherIcon(hour.conditions, hour.is_daytime)}
                        </span>
                        <span className="hourly-temp">{hour.temp}°</span>
                        {hour.precipitation_chance > 20 && (
                          <span className="hourly-precip">💧{hour.precipitation_chance}%</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* 5-Day Forecast */}
              {detailedWeather.daily && detailedWeather.daily.length > 0 && (
                <div className="weather-daily">
                  <h4>5-Day Forecast</h4>
                  <div className="daily-list">
                    {detailedWeather.daily.map((day, idx) => (
                      <div key={idx} className="daily-row">
                        <span className="daily-name">{day.short_name || day.name.split(' ')[0]}</span>
                        <span className="daily-icon">{getWeatherIcon(day.conditions)}</span>
                        <span className="daily-conditions">{day.conditions}</span>
                        <span className="daily-temps">
                          <span className="daily-high">{day.high}°</span>
                          <span className="daily-low">{day.low}°</span>
                        </span>
                        {day.precipitation_chance > 20 && (
                          <span className="daily-precip">💧{day.precipitation_chance}%</span>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Animal-Specific Alerts with Checklists */}
              {detailedWeather.alerts && detailedWeather.alerts.length > 0 && (
                <div className="weather-animal-alerts-detailed">
                  <h4>Your Animals</h4>
                  {detailedWeather.alerts.map((alert, idx) => {
                    const alertId = `alert-${alert.animal.id}-${idx}`;
                    const isExpanded = selectedAlert === alertId;
                    return (
                      <div
                        key={alertId}
                        className={`animal-alert-card ${alert.type} ${isExpanded ? 'expanded' : ''}`}
                      >
                        <div
                          className="animal-alert-header"
                          onClick={() => setSelectedAlert(isExpanded ? null : alertId)}
                        >
                          <span className="animal-alert-icon">{alert.icon}</span>
                          <div className="animal-alert-info">
                            <span className="animal-alert-title">{alert.title}</span>
                            <span className="animal-alert-subtitle">{alert.subtitle}</span>
                          </div>
                          <span className="animal-alert-expand">{isExpanded ? '▼' : '▶'}</span>
                        </div>

                        {isExpanded && (
                          <div className="animal-alert-content">
                            {/* Checklist */}
                            {alert.checklist && alert.checklist.length > 0 && (
                              <div className="animal-checklist">
                                {alert.checklist.map((item, taskIdx) => {
                                  const key = `${alertId}-${taskIdx}`;
                                  const isChecked = checkedItems.has(key);
                                  return (
                                    <label
                                      key={taskIdx}
                                      className={`checklist-item ${item.priority} ${isChecked ? 'checked' : ''}`}
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isChecked}
                                        onChange={() => toggleCheckItem(alertId, taskIdx)}
                                      />
                                      <span className="checklist-task">{item.task}</span>
                                      {item.priority === 'high' && (
                                        <span className="priority-badge">!</span>
                                      )}
                                    </label>
                                  );
                                })}
                              </div>
                            )}

                            {/* Feed Tip */}
                            {alert.feed_tip && (
                              <div className="animal-feed-tip">
                                <span className="feed-tip-icon">💡</span>
                                <span>{alert.feed_tip}</span>
                              </div>
                            )}
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              )}

              {/* General Tips */}
              {detailedWeather.general_tips && detailedWeather.general_tips.length > 0 && (
                <div className="weather-general-tips">
                  <h4>General Tips</h4>
                  <ul>
                    {detailedWeather.general_tips.map((tip, idx) => (
                      <li key={idx}>{tip}</li>
                    ))}
                  </ul>
                </div>
              )}
            </>
          ) : (
            // Fallback to basic forecast if detailed fails
            <>
              {upcoming.length > 0 && (
                <div className="weather-forecast">
                  {upcoming.map((day, idx) => (
                    <div key={idx} className="weather-forecast-day">
                      <span className="forecast-name">{day.name.split(' ')[0]}</span>
                      <span className="forecast-icon">{getWeatherIcon(day.conditions)}</span>
                      <span className="forecast-temps">
                        <span className="forecast-high">{day.high}°</span>
                        <span className="forecast-low">{day.low}°</span>
                      </span>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Basic Animal Weather Alerts (when not expanded) */}
      {!expanded && weather.animal_alerts && weather.animal_alerts.length > 0 && (
        <div className="animal-alerts">
          {weather.animal_alerts.map((alert, idx) => (
            <div
              key={idx}
              className={`animal-alert ${alert.severity}`}
              onClick={handleExpand}
              style={{ cursor: 'pointer' }}
            >
              <span className="animal-alert-icon">{alert.icon}</span>
              <span className="animal-alert-message">{alert.message}</span>
              <span className="animal-alert-more">Details →</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default WeatherWidget;
